import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Utensils, ChefHat, Hotel, Package, 
  BarChart3, DollarSign, PartyPopper, Users, MapPin, 
  BedDouble, Sun, Moon, Menu, X, LogOut, ShieldAlert
} from 'lucide-react';
import StockAlerts from '../StockAlerts';
import NetworkIndicator from '../NetworkIndicator';

function NavLink({ to, icon: Icon, children, onClick }) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to) && to !== '/' || (location.pathname === '/' && to === '/');
  
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden ${
        isActive 
          ? 'text-white shadow-lg shadow-primary/30' 
          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white'
      }`}
    >
      {isActive && (
        <motion.div 
          layoutId="activeNavBackground"
          className="absolute inset-0 bg-primary z-0"
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
      <div className="relative z-10 flex items-center space-x-3">
        <Icon size={20} className={`${isActive ? 'text-white' : 'text-slate-500 group-hover:text-primary dark:text-slate-400 dark:group-hover:text-primary-foreground'} transition-colors`} />
        <span className="font-medium whitespace-nowrap">{children}</span>
      </div>
    </Link>
  );
}

const Layout = ({ currentUser, theme, toggleTheme, handleLogout, children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const location = useLocation();

  useEffect(() => {
    if (window.innerWidth < 1024) setIsSidebarOpen(false);
  }, [location]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isSidebarOpen]);

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Inicio', roles: 'ALL' },
    { to: '/restaurant', icon: Utensils, label: 'Mesas', roles: ['ADMIN', 'WAITER'] },
    { to: '/events', icon: PartyPopper, label: 'Eventos', roles: ['ADMIN', 'WAITER'] },
    { to: '/kitchen', icon: ChefHat, label: 'Cocina', roles: ['ADMIN', 'KITCHEN'] },
    { to: '/caja', icon: DollarSign, label: 'Caja', roles: ['ADMIN', 'CASHIER'] },
    { to: '/hotel', icon: Hotel, label: 'Hotel', roles: ['ADMIN', 'RECEPTIONIST'] },
    { to: '/inventory', icon: Package, label: 'Productos', roles: ['ADMIN'] },
    { to: '/ingredients', icon: Package, label: 'Ingredientes', roles: ['ADMIN'] },
    { to: '/reports', icon: BarChart3, label: 'Reportes', roles: ['ADMIN'] },
    { to: '/tables', icon: MapPin, label: 'Conf. Mesas', roles: ['ADMIN'] },
    { to: '/rooms', icon: BedDouble, label: 'Conf. Habitaciones', roles: ['ADMIN'] },
    { to: '/users', icon: Users, label: 'Usuarios', roles: ['ADMIN'] },
    { to: '/expenses', icon: DollarSign, label: 'Control Gastos', roles: ['ADMIN'] },
    { to: '/audit', icon: ShieldAlert, label: 'Auditoría', roles: ['ADMIN'] },
  ];

  const allowedNavItems = navItems.filter(item => 
    item.roles === 'ALL' || (Array.isArray(item.roles) && item.roles.includes(currentUser.role))
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden relative transition-colors duration-300 w-full text-foreground">
      {/* Background Decorators */}
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/20 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-500/20 rounded-full blur-[150px] pointer-events-none" />
      
      <NetworkIndicator />

      {/* Overlay for Mobile Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && window.innerWidth < 1024 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar Navigation */}
      <AnimatePresence mode="wait">
        {(isSidebarOpen || window.innerWidth >= 1024) && (
          <motion.aside 
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
            className={`fixed inset-y-0 left-0 z-50 w-72 glass-panel border-r border-slate-200/50 dark:border-white/10 flex flex-col shadow-[8px_0_30px_rgba(0,0,0,0.05)] dark:shadow-[8px_0_30px_rgba(0,0,0,0.3)] rounded-none lg:rounded-r-3xl my-0 lg:my-4 lg:ml-4 lg:h-[calc(100vh-2rem)]`}
          >
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200/50 dark:border-white/5 shrink-0">
              <Link to="/" className="flex items-center space-x-3 group outline-none" onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)}>
                <div className="w-11 h-11 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg group-hover:shadow-primary/50 transition-all duration-300 transform group-hover:scale-105 group-hover:rotate-3">
                  <LayoutDashboard className="text-white" size={24} />
                </div>
                <h1 className="text-xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300">
                  Sistema Hotel <br/><span className="text-sm opacity-80 font-medium">& Restaurant</span>
                </h1>
              </Link>
              <button 
                onClick={() => setIsSidebarOpen(false)}
                className="lg:hidden p-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-slate-200 dark:border-white/5 shrink-0">
              <div className="flex items-center justify-between bg-slate-100 dark:bg-white/5 p-3 rounded-xl border border-slate-200 dark:border-white/5">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold shrink-0">
                    {currentUser.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col truncate">
                    <span className="text-sm font-bold text-slate-900 dark:text-white truncate">{currentUser.name}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{currentUser.role}</span>
                  </div>
                </div>
                <button onClick={toggleTheme} className="p-2 text-slate-500 hover:text-primary dark:text-slate-400 dark:hover:text-primary-foreground rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors shrink-0">
                  {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                </button>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-1 relative">
              {allowedNavItems.map((item) => (
                <NavLink key={item.to} to={item.to} icon={item.icon} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)}>
                  {item.label}
                </NavLink>
              ))}
            </div>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-white/5 shrink-0">
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-rose-500 hover:text-white bg-rose-50 hover:bg-rose-500 dark:bg-rose-500/10 dark:hover:bg-rose-500 rounded-xl transition-colors shadow-sm"
              >
                <LogOut size={18} />
                Cerrar Sesión
              </motion.button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <motion.div 
        layout
        className={`flex-1 flex flex-col min-w-0 h-full relative z-10 w-full overflow-hidden transition-all duration-300 ${isSidebarOpen && window.innerWidth >= 1024 ? 'lg:ml-72' : 'ml-0'}`}
      >
        <header className="flex items-center justify-between p-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-white/5 shadow-sm sticky top-0 z-30">
          <div className="flex items-center gap-4">
            {!isSidebarOpen && (
              <motion.button 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg transition-colors"
              >
                <Menu size={24} />
              </motion.button>
            )}
            {!isSidebarOpen && (
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 font-bold text-slate-900 dark:text-white hidden lg:flex"
              >
                <LayoutDashboard className="text-primary" size={20} />
                Sistema Hotel
              </motion.div>
            )}
          </div>
          <div className="flex items-center">
             {['ADMIN', 'KITCHEN'].includes(currentUser.role) && <StockAlerts compact />}
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-6 lg:p-8 w-full relative custom-scrollbar">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </motion.div>
    </div>
  );
};

export default Layout;
